import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Clock, CheckCircle, XCircle, Gift, User } from 'lucide-react'

interface PendingRedemption {
  id: string
  customer_id: string
  reward_assignment_id: string | null
  redemption_code: string
  redeemed_at: string
  status: 'pending' | 'approved' | 'rejected'
  source: 'customer' | 'reward'
  customer: {
    full_name: string
    email: string
    tenant_id: string
  }
  reward_assignment: {
    referral_count: number
    reward: {
      name: string
      description: string
      reward_type: string
      reward_value: string
      admin_approval_required: boolean
    }
  }
}

const RedemptionTasksWidget = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [pendingRedemptions, setPendingRedemptions] = useState<PendingRedemption[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      fetchPendingRedemptions()
    }
  }, [user])

  const fetchPendingRedemptions = async () => {
    if (!user?.id) return

    try {
      console.log('🔍 Fetching pending redemptions for user:', user.id)

      // Step 1: Fetch pending redemptions from both tables without joins or tenant filters
      const [custRedemptionsResp, rewardRedemptionsResp] = await Promise.allSettled([
        supabase
          .from('customer_reward_redemptions')
          .select('id, customer_id, reward_assignment_id, redemption_code, redeemed_at, status')
          .eq('status', 'pending'),
        supabase
          .from('reward_redemptions')
          .select('id, customer_id, reward_id, redemption_code, redeemed_at, status, tenant_id')
          .eq('status', 'pending')
      ])

      let custRedemptions: any[] = []
      let rewardRedemptions: any[] = []

      if (custRedemptionsResp.status === 'fulfilled' && !custRedemptionsResp.value.error) {
        custRedemptions = custRedemptionsResp.value.data || []
        console.log('✅ Found customer_reward_redemptions:', custRedemptions.length)
      } else {
        console.warn('❌ Error fetching customer_reward_redemptions:', custRedemptionsResp.status === 'fulfilled' ? custRedemptionsResp.value.error : custRedemptionsResp.reason)
      }

      if (rewardRedemptionsResp.status === 'fulfilled' && !rewardRedemptionsResp.value.error) {
        rewardRedemptions = rewardRedemptionsResp.value.data || []
        console.log('✅ Found reward_redemptions:', rewardRedemptions.length)
      } else {
        console.warn('❌ Error fetching reward_redemptions:', rewardRedemptionsResp.status === 'fulfilled' ? rewardRedemptionsResp.value.error : rewardRedemptionsResp.reason)
      }

      // Step 2: Get all unique customer IDs from both redemption types
      const allCustomerIds = [...new Set([
        ...custRedemptions.map(r => r.customer_id),
        ...rewardRedemptions.map(r => r.customer_id)
      ])].filter(Boolean)

      if (allCustomerIds.length === 0) {
        console.log('ℹ️ No customer IDs found in redemptions')
        setPendingRedemptions([])
        setLoading(false)
        return
      }

      // Step 3: Load customers and filter by current user's accessible tenants
      const { data: allCustomers, error: customersError } = await supabase
        .from('customers')
        .select('id, full_name, email, tenant_id')
        .in('id', allCustomerIds)

      if (customersError) {
        console.warn('❌ Error fetching customers:', customersError)
        setLoading(false)
        return
      }

      // Filter customers based on RLS - they should only return customers the current user can access
      const accessibleCustomers = allCustomers || []
      const accessibleCustomerIds = accessibleCustomers.map(c => c.id)
      
      console.log('✅ Accessible customers:', accessibleCustomers.length)

      if (accessibleCustomerIds.length === 0) {
        console.log('ℹ️ No accessible customers found')
        setPendingRedemptions([])
        setLoading(false)
        return
      }

      // Step 4: Filter redemptions to only include accessible customers
      const filteredCustRedemptions = custRedemptions.filter(r => accessibleCustomerIds.includes(r.customer_id))
      const filteredRewardRedemptions = rewardRedemptions.filter(r => accessibleCustomerIds.includes(r.customer_id))

      // Step 5: Load additional data separately - fix the reward loading
      const rewardAssignmentIds = filteredCustRedemptions.map(r => r.reward_assignment_id).filter(Boolean)
      const rewardIds = filteredRewardRedemptions.map(r => r.reward_id).filter(Boolean)

      console.log('🔗 Loading assignments for IDs:', rewardAssignmentIds)
      console.log('🎁 Loading direct rewards for IDs:', rewardIds)

      // First load assignments to get their reward_ids
      const { data: assignments, error: assignmentsError } = rewardAssignmentIds.length > 0 
        ? await supabase
            .from('referral_reward_assignments')
            .select('id, reward_id, referral_count, tenant_id')
            .in('id', rewardAssignmentIds)
        : { data: [], error: null }

      if (assignmentsError) {
        console.warn('❌ Error loading assignments:', assignmentsError)
      }

      // Get reward IDs from assignments
      const assignmentRewardIds = (assignments || []).map((a: any) => a.reward_id).filter(Boolean)
      const allRewardIds = [...new Set([...assignmentRewardIds, ...rewardIds])]

      console.log('🎁 Loading all rewards for IDs:', allRewardIds)

      // Load all rewards at once
      const { data: rewards, error: rewardsError } = allRewardIds.length > 0
        ? await supabase
            .from('rewards')
            .select('id, name, description, reward_type, reward_value, admin_approval_required')
            .in('id', allRewardIds)
        : { data: [], error: null }

      if (rewardsError) {
        console.warn('❌ Error loading rewards:', rewardsError)
      }

      // Create lookup maps
      const customersMap = new Map(accessibleCustomers.map(c => [c.id, c]))
      const assignmentsMap = new Map((assignments || []).map((a: any) => [a.id, a]))
      const rewardsMap = new Map((rewards || []).map((r: any) => [r.id, r]))

      console.log('📊 Lookup maps created - customers:', customersMap.size, 'assignments:', assignmentsMap.size, 'rewards:', rewardsMap.size)

      // Step 6: Build unified PendingRedemption array
      const pendingList: PendingRedemption[] = []

      // Process customer_reward_redemptions
      for (const redemption of filteredCustRedemptions) {
        const customer = customersMap.get(redemption.customer_id)
        const assignment = assignmentsMap.get(redemption.reward_assignment_id)
        
        // Find reward by assignment's reward_id
        let reward = null
        if (assignment?.reward_id) {
          reward = rewardsMap.get(assignment.reward_id)
        }

        if (!customer) continue

        // Include all pending redemptions for accessible customers
        pendingList.push({
            id: redemption.id,
            customer_id: redemption.customer_id,
            reward_assignment_id: redemption.reward_assignment_id,
            redemption_code: redemption.redemption_code,
            redeemed_at: redemption.redeemed_at,
            status: redemption.status,
            source: 'customer',
            customer: {
              full_name: customer.full_name,
              email: customer.email,
              tenant_id: customer.tenant_id
            },
            reward_assignment: {
              referral_count: assignment?.referral_count || 0,
              reward: {
                name: reward?.name || 'Empfehlungsbelohnung',
                description: reward?.description || 'Belohnung für Empfehlungen',
                reward_type: reward?.reward_type || 'referral',
                reward_value: reward?.reward_value || 'Unbekannt',
                admin_approval_required: reward?.admin_approval_required ?? true
              }
            }
          })
      }

      // Process reward_redemptions
      for (const redemption of filteredRewardRedemptions) {
        const customer = customersMap.get(redemption.customer_id)
        const reward = rewardsMap.get(redemption.reward_id)

        if (!customer) continue

        // Include all pending redemptions for accessible customers
        pendingList.push({
            id: redemption.id,
            customer_id: redemption.customer_id,
            reward_assignment_id: null,
            redemption_code: redemption.redemption_code,
            redeemed_at: redemption.redeemed_at,
            status: redemption.status,
            source: 'reward',
            customer: {
              full_name: customer.full_name,
              email: customer.email,
              tenant_id: customer.tenant_id
            },
            reward_assignment: {
              referral_count: 0,
              reward: {
                name: reward?.name || 'Belohnung',
                description: reward?.description || 'Einlösbare Belohnung',
                reward_type: reward?.reward_type || 'reward',
                reward_value: reward?.reward_value || 'Unbekannt',
                admin_approval_required: reward?.admin_approval_required ?? true
              }
            }
          })
      }

      console.log('✅ Final pending redemptions:', pendingList.length)
      setPendingRedemptions(pendingList)
    } catch (error) {
      console.error('Error fetching pending redemptions:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Ausstehende Belohnungen konnten nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleApproveRedemption = async (redemption: PendingRedemption) => {
    setProcessing(redemption.id)
    try {
      if (redemption.source === 'customer') {
        const { error } = await supabase
          .from('customer_reward_redemptions')
          .update({
            status: 'approved',
            approved_by: user?.id,
            approved_at: new Date().toISOString()
          })
          .eq('id', redemption.id)
        if (error) throw error
      } else {
        // For reward_redemptions table: approved = used, rejected = expired
        const { error } = await supabase
          .from('reward_redemptions')
          .update({ 
            status: 'used',
            used_at: new Date().toISOString()
          })
          .eq('id', redemption.id)
        if (error) throw error
      }

      toast({
        title: "Belohnung genehmigt",
        description: "Die Belohnung wurde erfolgreich genehmigt.",
      })

      // Refresh the list
      await fetchPendingRedemptions()
    } catch (error) {
      console.error('Error approving redemption:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: `Die Belohnung konnte nicht genehmigt werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
      })
    } finally {
      setProcessing(null)
    }
  }

  const handleRejectRedemption = async (redemption: PendingRedemption) => {
    setProcessing(redemption.id)
    try {
      if (redemption.source === 'customer') {
        const { error } = await supabase
          .from('customer_reward_redemptions')
          .update({
            status: 'rejected',
            approved_by: user?.id,
            approved_at: new Date().toISOString()
          })
          .eq('id', redemption.id)
        if (error) throw error
      } else {
        // For reward_redemptions table: approved = used, rejected = expired
        const { error } = await supabase
          .from('reward_redemptions')
          .update({ 
            status: 'expired'
          })
          .eq('id', redemption.id)
        if (error) throw error
      }

      toast({
        title: "Belohnung abgelehnt",
        description: "Die Belohnung wurde abgelehnt.",
      })

      // Refresh the list
      await fetchPendingRedemptions()
    } catch (error) {
      console.error('Error rejecting redemption:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: `Die Belohnung konnte nicht abgelehnt werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
      })
    } finally {
      setProcessing(null)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Ausstehende Belohnungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Wird geladen...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Ausstehende Belohnungen
          {pendingRedemptions.length > 0 && (
            <Badge variant="destructive" className="ml-2">
              {pendingRedemptions.length}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Alle ausstehenden Belohnungen
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {pendingRedemptions.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            Keine ausstehenden Belohnungen
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kunde</TableHead>
                <TableHead>Belohnung</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingRedemptions.map((redemption) => (
                <TableRow key={redemption.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium text-sm">{redemption.customer.full_name}</div>
                        <div className="text-xs text-muted-foreground">{redemption.customer.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-primary" />
                      <div>
                        <div className="font-medium text-sm">{redemption.reward_assignment.reward.name}</div>
                        <div className="text-xs text-muted-foreground">{redemption.reward_assignment.reward.reward_value}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{redemption.redemption_code}</code>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{new Date(redemption.redeemed_at).toLocaleDateString('de-DE')}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-warning/10 text-warning">
                      Wartend
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        onClick={() => handleApproveRedemption(redemption)}
                        disabled={processing === redemption.id}
                        className="bg-success hover:bg-success/90 h-8 px-2"
                      >
                        <CheckCircle className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRejectRedemption(redemption)}
                        disabled={processing === redemption.id}
                        className="h-8 px-2"
                      >
                        <XCircle className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

export default RedemptionTasksWidget